import { useMemo } from "react";
import * as d3 from "d3";
import { animated, useSpring } from '@react-spring/web';
import React from "react";
import { DataChartItem } from "@models/DataChartItem";
import { CATEGORY_COLORS, FRANCE_REGIONS, ITEMS_COLOR, OPACITY_VALUE } from "@utils/constants";
import styled from "styled-components";
import { formatTotal } from "@utils/formatData";
import { useViewportMode } from "@utils/useMobileDetection";
import AnimatedDiv from "./AnimatedDiv";

type SliceProps = {
  color?: string;
  radius: number;
  innerRadius: number;
  slice: d3.PieArcDatum<DataChartItem>;
  currentCategory?: string;
  total: number;
};
type DonutChartProps = {
  data: DataChartItem[];
  className?: string;
  total: number;
  currentCategory?: string;
  size: string;
};
const StyledDiv = styled.div<{ size: string; }>`
  .pie-chart-container {
    position: relative;
  }
  .inner-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    .total-txt {
      font-weight: 800;
    }
    .unit {
      font-weight: 400;
    }
      z-index: 10;
  }
`;
const Slice = ({ slice, radius, color, innerRadius, currentCategory, total }: SliceProps) => {
  const arcPathGenerator = d3.arc();
  const springProps = useSpring({
    to: {
      pos: [slice.startAngle, slice.endAngle] as [number, number],
      config: {
        duration: 30000
       }
    },
  });
  const props = {
    d: springProps.pos.to((start, end) => {
      return arcPathGenerator({
        innerRadius,
        outerRadius: radius,
        startAngle: start,
        endAngle: end,
      });
    }),
    style: { fill: color, config: {
      duration: 30000
     } }
  }

  const [styles, api] = useSpring(() => ({
      opacity: 0.7,
      pos: [slice.startAngle, slice.endAngle] as [number, number],
      fill: color,
      config: {
        duration: 300
       }
      }), []
    );

    React.useEffect(() => {
      api.start({
          to: [
              { opacity: 0.7, pos: [slice.startAngle, slice.endAngle] as [number, number],
                fill: color },
              { opacity: 1, pos: [slice.startAngle, slice.endAngle] as [number, number],
                fill: color },
            ]

      })
    }, [currentCategory, api]);
  return (
    <>
    <animated.path {...props} style={{
      ...props.style,
      ...( [0, -1].includes(total) ? {fill: '#000000', fillOpacity: OPACITY_VALUE} : {
        fillOpacity: CATEGORY_COLORS[slice.data.name].toUpperCase() === currentCategory || currentCategory === 'TOTAL' ? 1 : OPACITY_VALUE
      }),
    }} />
    {![0, -1].includes(total) && <animated.path {...props} style={{
      ...styles,
      fill: '#000000',
      fillOpacity: CATEGORY_COLORS[slice.data.name].toUpperCase() === currentCategory || currentCategory === 'TOTAL' ? 0 : OPACITY_VALUE
    }} />}
    </>
  );
};


const DonutChart = ({ data, className, total, currentCategory, size }: DonutChartProps) => {
  let outerRadius = 0;
  let innerRadius = 0;
  const { isTablet, isMobile } = useViewportMode();
  const isOverviewDonut = size === 'SIZE_6';

  if (isOverviewDonut) {
    // Overview donut in Capacities -- NOT scaled by map transform
    if (isTablet) {
      outerRadius = 30;
      innerRadius = 18;
    } else if (isMobile) {
      outerRadius = 45;
      innerRadius = 27;
    } else {
      outerRadius = 59;
      innerRadius = 36;
    }
  } else {
    // Map donuts -- always use desktop sizes since container is scaled via CSS transform
    switch (size) {
      case "SIZE_1":
        outerRadius = 25.5;
        innerRadius = 15.5;
        break;
      case "SIZE_2":
        outerRadius = 31;
        innerRadius = 19;
        break;
      case "SIZE_3":
        outerRadius = 36.5;
        innerRadius = 22.5;
        break;
      case "SIZE_4":
        outerRadius = 50;
        innerRadius = 30;
        break;
      case "SIZE_5":
        outerRadius = 42;
        innerRadius = 25.5;
        break;
      default:
        outerRadius = 59;
        innerRadius = 36;
        break;
    }
  }

  const pie = useMemo(() => {
    const pieGenerator = d3
      .pie<any, DataChartItem>()
      .value((d) => d.value || 0)
      .sort(null);
    return pieGenerator(data);
  }, [data]);
  const allPaths = pie.map((slice, i) => {
    return (
      <Slice
        key={slice.data.name}
        radius={outerRadius}
        innerRadius={innerRadius}
        slice={slice}
        color={ITEMS_COLOR[slice.data.name]}
        currentCategory={currentCategory}
        total={total}
      />
    );
  });

  // Map donut positions -- desktop-only values, scaling is handled by CSS transform on the map container
  const regionPositioning = {
    auvergne_rhone_alpes: 'top-[403px] left-[444px]',
    bourgogne_franche_comte: 'top-[270px] left-[459px]',
    bretagne: 'top-[177px] left-[55px]',
    centre_val_de_loire: 'top-[246px] left-[293px]',
    grand_est: 'top-[145px] left-[494px]',
    hauts_de_france: 'top-[48px] left-[350px]',
    ile_de_france: 'top-[146px] left-[336px]',
    normandie: 'top-[122px] left-[219px]',
    nouvelle_aquitaine: 'top-[409px] left-[211px]',
    occitanie: 'top-[544px] left-[306px]',
    pays_de_la_loire: 'top-[233px] left-[157px]',
    provence_alpes_cote_d_azur: 'top-[524px] left-[532px]',
  };

  const regionClass = className?.replace('.', '');
  return (
    <StyledDiv className={`
      ${regionClass || ''}
      flex justify-center items-center group
      ${size === 'SIZE_6' ? (isTablet ? 'h-[56px] w-full' : 'h-[120px] w-full') : 'h-[86px] w-[86px]'}
      ${regionClass ? `absolute ${regionPositioning[regionClass] || ''}` : ''}
    `}
    size={size} >

      <div className={`pie-chart-container col-12`} style={{ width: outerRadius * 2, height: outerRadius * 2}}>
        <svg width={outerRadius * 2} height={outerRadius * 2} style={{ display: "inline-block" }}>
          <g transform={`translate(${outerRadius}, ${outerRadius})`}>
            {allPaths}
            <circle r={innerRadius} fill="#ffffff"/>
          </g>
        </svg>
        <div className={`
          inner-text
          ${size === 'SIZE_6' ? (isTablet ? 'leading-[12px]' : isMobile ? 'leading-[16px]' : 'leading-[22px]') : 'leading-[16px]'}
        `}
        >
        {total === -1 ? (
          <AnimatedDiv>NC</AnimatedDiv>
        ) : (
          <>
            <span className={`
          total-txt
          ${size === 'SIZE_6' ? (isTablet ? 'text-[13px]' : isMobile ? 'text-[17.5px]' : 'text-[26.5px]') : 'text-[16px]'}
        `}><AnimatedDiv>{formatTotal(total)}</AnimatedDiv></span>
            <br />
            <span className={`
          unit
          ${size === 'SIZE_6' ? (isTablet ? 'text-[9px]' : isMobile ? 'text-[13px]' : 'text-[19.6px]') : 'text-[12px]'}
        `}>GW</span>
          </>
        )}
      </div>
      </div>

      {className && <div className="absolute z-50 hidden group-hover:block whitespace-nowrap break-words rounded-lg bg-black py-1.5 px-3 font-sans text-sm font-normal text-white" style={{bottom: `${outerRadius + 42}px`}}>
        {FRANCE_REGIONS[className]}
      </div>}
    </StyledDiv>
  );
};


export default DonutChart;
